<template>
  <div class="animated fadeIn">
    <b-card class="no-border animated">
      <div class="item-header clearfix mb-3">
        <h5 v-if="isEdit" class="pull-left mb-0 mt-2">Edit Question: {{name}}</h5>
        <h5 v-if="!isEdit" class="pull-left mb-0 mt-2">Create New Question</h5>
      </div>
      <b-form @submit.prevent="onSubmit()">
        <div class="row mb-2">
          <div class="col-md-6">
            <label>Name</label>
            <input
              v-model="form.name"
              class="form-control"
              type="text"
              name="prestart-name"
              v-validate="'required'"
              :class="{'is-invalid': errors.has('prestart-name')}"
              placeholder="Name">
            <b-form-feedback>{{ errors.first('prestart-name') }}</b-form-feedback>
          </div>
          <div class="form-group col-md-6">
            <label>Company</label>
            <multiselect
              name="company"
              v-validate="'required'"
              :class="{'multiselect-danger': errors.has('company')}"
              v-model="companyModel"
              :options="companies"
              label="name"
              track-by="id"
              :disabled="isAddFromSelectedCompany"
              placeholder="Select company"/>
          </div>
        </div>
        <div class="row mb-2">
          <div class="col-md-12">
            <label>Question</label>
            <input
              v-model="form.question"
              class="form-control"
              type="text"
              name="question"
              v-validate="'required'"
              :class="{'is-invalid': errors.has('question')}"
              placeholder="Question">
            <b-form-feedback>{{ errors.first('question') }}</b-form-feedback>
          </div>
        </div>
        <div class="row mb-2">
          <div class="col-md-12">
            <label>Error Message</label>
            <input
              v-model="form.error_text"
              class="form-control"
              type="text"
              name="error-message"
              v-validate="'required'"
              :class="{'is-invalid': errors.has('error-message')}"
              placeholder="Error Message">
          </div>
        </div>
        <div class="row mb-2">
          <div class="col-md-12">
            <b-form-select
              v-model="form.type"
              name="question-type"
              v-validate="'required'"
              :class="{'is-invalid': errors.has('question-type')}"
              :options="typeOptions"></b-form-select>
          </div>
        </div>
        <div class="row mb-2">
          <div class="col-md-12">
            <b-form-checkbox
              v-model="required_status"
              name="question-required"
              value="required"
              unchecked-value="not_required"
            > Required Question </b-form-checkbox>
          </div>
        </div>
        <div v-if="form.type === 'FILL'">
          <div class="row mb-2">
            <div class="col-md-6">
              <b-form-select
                v-model="typeFillAnswer"
                name="type-answer-fill"
                v-validate="'required'"
                :class="{'is-invalid': errors.has('type-answer-fill')}"
                :options="typeFillAnswerOptions"></b-form-select>
            </div>
            <div class="col-md-6">
              <input
                v-if="typeFillAnswer === 'text' || typeFillAnswer === 'password'"
                v-model="defaultFillAnswer"
                class="form-control"
                type="text"
                name="default-answer-fill"
                v-validate="'required'"
                :class="{'is-invalid': errors.has('default-answer-fill')}"
                placeholder="Default Answer">
              <input
                v-if="typeFillAnswer === 'number'"
                v-model="defaultFillAnswer"
                class="form-control"
                type="number"
                name="default-answer-fill"
                v-validate="'required|numeric'"
                :class="{'is-invalid': errors.has('default-answer-fill')}"
                placeholder="Default Answer">
              <input
                v-if="typeFillAnswer === 'email'"
                v-model="defaultFillAnswer"
                class="form-control"
                type="email"
                name="default-answer-fill"
                v-validate="'required|email'"
                :class="{'is-invalid': errors.has('default-answer-fill')}"
                placeholder="Default Answer">
            </div>
          </div>
        </div>
        <div v-else-if="form.type === 'CHOICE'">
          <div class="row mb-2">
            <div class="col-md-12">
              <div class="item-header clearfix mb-3">
                <h5 class="pull-left mb-0 mt-2">Answer Options</h5>
                <div class="pull-right">
                  <b-button @click="addAnswerChoice(form.type)" variant="outline-primary">
                    <i class="fa fa-plus"></i>
                    &nbsp; Add answer option
                    </b-button>
                </div>
              </div>
              <table class="table">
                <thead>
                  <tr>
                    <td><strong>Option Text</strong></td>
                    <td><strong>Default Answer</strong></td>
                    <td><strong>Action</strong></td>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="(choice, index) in optionsChoiceAnswer" :key="index">
                    <td>
                      <input
                        v-model="choice.text"
                        class="form-control"
                        placeholder="Option text"
                        type="text">
                    </td>
                    <td>
                      <b-form-radio
                        @change="onChangeDefaultChoiceAnswer"
                        name="choice-answer-radios"
                        v-model="defaultChoiceAnswer"
                        :value="choice.text">Set as default answer</b-form-radio>
                    </td>
                    <td>
                      <b-button
                        @click="removeAnswerChoice(form.type, index)"
                        :disabled="optionsChoiceAnswer.length === 1"
                        variant="outline-danger">
                        <i class="fa fa-remove"></i>&nbsp; Delete
                      </b-button>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
        <div v-else-if="form.type === 'MULTI_CHOICE'">
          <div class="row mb-2">
            <div class="col-md-12">
              <div class="item-header clearfix mb-3">
                <h5 class="pull-left mb-0 mt-2">Answer Options</h5>
                <div class="pull-right">
                  <b-button @click="addAnswerChoice(form.type)" variant="outline-primary">
                    <i class="fa fa-plus"></i>
                    &nbsp; Add answer option
                    </b-button>
                </div>
              </div>
              <table class="table">
                <thead>
                  <tr>
                    <td><strong>Option Text</strong></td>
                    <td><strong>Default Answer</strong></td>
                    <td><strong>Action</strong></td>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="(choice, index) in optionsMultiChoiceAnswer" :key="index">
                    <td>
                      <input
                        v-model="choice.text"
                        class="form-control"
                        placeholder="Option text"
                        type="text">
                    </td>
                    <td>
                      <b-form-checkbox
                        @change="onChangeDefaultMultiChoiceAnswer($event, choice.text)"
                        v-model="defaultMultiChoiceAnswer"
                        name="multi-choice-answer-checkbox"
                        :value="choice.text">Set as default answer</b-form-checkbox>
                    </td>
                    <td>
                      <b-button
                        @click="removeAnswerChoice(form.type, index)"
                        :disabled="optionsMultiChoiceAnswer.length === 1"
                        variant="outline-danger">
                        <i class="fa fa-remove"></i>&nbsp; Delete
                      </b-button>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
        <div v-else-if="form.type === 'THREE_CHOICES'">
          <div class="row mb-2">
            <div class="col-md-12">
              <div class="row mb-2">
                <div class="col-md-12">
                  <b-form-checkbox
                    v-model="nextQuestion"
                    name="button-next"
                    value="next"
                    unchecked-value="not_next"
                  > Next question when answer is bad </b-form-checkbox>
                </div>
              </div>
              <div class="row mb-2">
                <div class="col-md-12">
                  <b-form-checkbox
                    v-model="randomizeButtons"
                    name="button-randomize"
                    value="randomize"
                    unchecked-value="not_randomize"
                  > Randomize buttons order </b-form-checkbox>
                </div>
              </div>
              <b-card v-for="(button, index) in optionsThreeChoiceAnswer" :key="index">
                <div class="mb-2">
                  <button
                    v-on:click.self.prevent
                    class="btn"
                    :style="{ 'background-color': button.background, 'color': 'white' }"
                    :class="button.key">{{ button.label }}</button>
                </div>
                <div class="row mb-2">
                  <div class="col-md-6">
                    <label>Button Label</label>
                    <input
                      v-model="button.label"
                      placeholder="Button Label"
                      class="form-control"
                      type="text">
                  </div>
                  <div class="col-md-6">
                    <label>Background Color</label>
                    <div class="form__input">
                      <swatches
                        v-model="button.background"
                        :colors="colorsPalette"
                        row-length="6"
                        popover-to="right"/>
                    </div>
                  </div>
                </div>
                <div class="row mb-2">
                  <div class="col-md-12">
                    <b-form-checkbox
                      v-if="button.key === 'b2'"
                      v-model="button.remove"
                      name="button-remove"
                      value="remove"
                      unchecked-value="not_remove"
                    > Remove this button </b-form-checkbox>
                    <b-form-checkbox
                      v-if="button.remove === 'not_remove' && button.key === 'b2' || button.key !== 'b2'"
                      v-model="button.break"
                      name="button-break"
                      value="break"
                      unchecked-value="not_break"
                    > Break after selected </b-form-checkbox>
                    <b-form-checkbox
                      v-if="button.remove === 'not_remove' && button.key === 'b2' || button.key !== 'b2'"
                      v-model="button.proof"
                      name="button-proof"
                      value="proof"
                      unchecked-value="not_proof"
                    > Need proof (photo and description) </b-form-checkbox>
                  </div>
                </div>
              </b-card>
            </div>
          </div>
        </div>
        <hr>
        <b-button type="submit" variant="primary">Save changes</b-button>
        <b-button
          @click="onCancel()"
          type="reset"
          variant="outline-secondary"
          class="pull-right">Cancel</b-button>
      </b-form>
    </b-card>
  </div>
</template>

<script>
import { isEmpty, find, isObject, isBoolean } from 'lodash';
import Swatches from 'vue-swatches';
import Multiselect from 'vue-multiselect';

export default {
  name: 'prestart_create_edit',
  components: {
    Swatches,
    Multiselect,
  },
  data() {
    return {
      companies: [],
      prestartName: null,
      question: null,
      questionType: null,
      errorMessage: null,
      defaultFillAnswer: null,
      defaultChoiceAnswer: null,
      defaultMultiChoiceAnswer: [],
      typeFillAnswer: null,
      required_status: 'not_required',
      nextQuestion: 'not_next',
      randomizeButtons: 'randomize',
      optionsChoiceAnswer: [{ text: 'Option 1' }],
      optionsMultiChoiceAnswer: [{ text: 'Option 1' }],
      optionsThreeChoiceAnswer: [
        {
          key: 'b1',
          label: 'Good',
          break: 'not_break',
          proof: 'not_proof',
          background: '#689ECE',
        },
        {
          key: 'b2',
          label: 'Fair',
          break: 'not_break',
          proof: 'proof',
          remove: 'not_remove',
          background: '#F1C40F',
        },
        {
          key: 'b3',
          label: 'Not Good',
          break: 'break',
          proof: 'not_proof',
          background: '#E74C3C',
        },
      ],
      colorsPalette: [
        ['#EF5350', '#EC407A', '#AB47BC', '#7E57C2', '#5C6BC0'],
        ['#42A5F5', '#26A69A', '#66BB6A', '#F1C40F', '#FFA726'],
        ['#FF7043', '#78909C', '#E74C3C', '#689ECE', '#000'],
      ],
      form: {
        company: null,
        name: '',
        question: '',
        type: null,
        required: false,
        error_text: '',
        metadata: {},
      },
      typeOptions: [
        { value: null, text: 'Please select question type' },
        { value: 'FILL', text: 'Fill' },
        { value: 'CHOICE', text: 'Choice' },
        { value: 'MULTI_CHOICE', text: 'Multiple Choices' },
        { value: 'THREE_CHOICES', text: 'Three Choices' },
      ],
      typeFillAnswerOptions: [
        { value: null, text: 'Please select answer type' },
        { value: 'text', text: 'Text' },
        { value: 'number', text: 'Number' },
        { value: 'password', text: 'Password' },
        { value: 'email', text: 'Email' },
      ],
    };
  },
  created() {
    const breadcrumb = [
      { to: '', name: 'Home' },
      { to: 'admin', name: 'Admin' },
      { to: 'prestart_checklist', name: 'Prestart Checklist' },
      { to: 'edit', name: 'Edit' },
    ];

    if (!this.isEdit) {
      breadcrumb.splice(3, 2);
      breadcrumb.push({ to: 'create', name: 'Create' });
    }

    this.$store.commit('companies/CLEAR_QUERY');
    // this.$store.dispatch('companies/LOAD_INFO').then(() => {
    //   this.$store.commit('companies/SET_QUERY_COUNT', this.company_count_all);
    //   this.$store.dispatch('companies/LOAD_DATA');
    // });
    this.$store.dispatch('companies/LOAD_TRANSPORTERS').then((companies) => {
      this.companies = companies;
      if (!this.isEdit) {
        const { company_id: id } = this.userData;
        this.companyModel = find(companies, { id: id.toString() });
      } else {
        const { prestartId } = this.$route.params;
        this.$store.commit('prestart_checklist/SET_DETAIL_ID', prestartId);
        this.$store.dispatch('prestart_checklist/LOAD_DETAIL_DATA');
      }
    });
    this.$store.commit('SET_BREADCRUMB_ITEMS', breadcrumb);
  },
  computed: {
    // companies() {
    //   const companies = this.$store.getters['companies/companies'];
    //   const companyIds = get(this.userData, 'company._companyIds');
    //   return this.isSystemAdmin ?
    //     companies : companies.filter(c => companyIds.indexOf(parseInt(c.id, 10)) > -1);
    // },
    isEdit() {
      return !isEmpty(this.$route.params.prestartId);
    },
    name() {
      return this.detailedInfo.name;
    },
    userData() {
      return this.$store.getters.userData;
    },
    company_count_all() {
      return this.$store.getters['companies/count_all'];
    },
    isSystemAdmin() {
      const { userData } = this.$store.getters;
      return userData.type === 'SYSTEM_ADMIN';
    },
    isAddFromSelectedCompany() {
      return !isEmpty(this.$route.params.companyId);
    },
    selectedCompany() {
      const { companyId: id } = this.$route.params;
      const selectedCompany = find(this.companies, { id });
      return selectedCompany;
    },
    companyModel: {
      get() {
        return this.selectedCompany ? this.selectedCompany : this.form.company;
      },
      set(company) {
        if (this.selectedCompany) {
          this.selectedCompany = company;
        } else {
          this.form.company = company;
        }
      },
    },
    detailedInfo() {
      const detailedInfo = this.isEdit
        ? this.$store.getters['prestart_checklist/get_detail_data']
        : {};
      return detailedInfo;
    },
  },
  watch: {
    detailedInfo(data) {
      const { form } = this;
      if (!isEmpty(data)) {
        const {
          name, question, type, error_text, required, metadata, company,
        } = data;
        form.name = name;
        form.question = question;
        form.type = type;
        form.error_text = error_text;
        form.metadata = metadata;
        this.required_status = required ? 'required' : 'not_required';
        form.company = company;

        if (type === 'FILL') {
          this.defaultFillAnswer = metadata.default_answer;
          this.typeFillAnswer = metadata.answer_type || null;
        } else if (type === 'CHOICE') {
          this.optionsChoiceAnswer = metadata.answer.map(a => ({ text: a }));
          this.onChangeDefaultChoiceAnswer(metadata.default_answer);
        } else if (type === 'MULTI_CHOICE') {
          this.optionsMultiChoiceAnswer = metadata.answer.map(a => ({ text: a }));
          metadata.default_answer.forEach((answer) => {
            this.onChangeDefaultMultiChoiceAnswer(answer, answer);
          });
        } else if (type === 'THREE_CHOICES') {
          this.optionsThreeChoiceAnswer.splice(0);
          this.nextQuestion = metadata.next ? 'next' : 'not_next';
          this.randomizeButtons = metadata.randomize ? 'randomize' : 'not_randomize';
          const { answer } = metadata;
          const keys = Object.keys(answer);
          keys.forEach((key) => {
            const btn = answer[key];
            const {
              break: br, label, proof, background,
            } = btn;
            let pushBtn = {};
            pushBtn = {
              key,
              label,
              background,
              break: br ? 'break' : 'not_break',
              proof: proof ? 'proof' : 'not_proof',
            };
            if (key === 'b2') pushBtn.remove = btn.remove ? 'remove' : 'not_remove';
            this.optionsThreeChoiceAnswer.push(pushBtn);
          });
        }
      }
    },
    required_status(value) {
      this.form.required = value === 'required';
    },
  },
  methods: {
    onSubmit() {
      this.validate().then((valid) => {
        const action = this.isEdit ? 'UPDATE_DATA' : 'CREATE_DATA';

        if (valid) {
          if (this.form.type === 'FILL') {
            this.form.metadata.default_answer = this.defaultFillAnswer;
            this.form.metadata.answer_type = this.typeFillAnswer;
          } else if (this.form.type === 'CHOICE') {
            this.form.metadata.answer = this.optionsChoiceAnswer.map(a => a.text);
            this.form.metadata.default_answer = this.defaultChoiceAnswer;
          } else if (this.form.type === 'MULTI_CHOICE') {
            const multiChoices = this.optionsMultiChoiceAnswer.map(a => a.text);
            this.form.metadata.answer = multiChoices;
            this.form.metadata.default_answer = this.defaultMultiChoiceAnswer
              .filter(a => multiChoices.includes(a));
          } else if (this.form.type === 'THREE_CHOICES') {
            const answer = {};
            this.optionsThreeChoiceAnswer.forEach((button) => {
              const {
                key, label, proof, break: br, background,
              } = button;
              answer[key] = {
                label,
                background,
                proof: proof === 'proof' ? 1 : 0,
                break: br === 'break' ? 1 : 0,
              };
              if (key === 'b2') {
                answer[key].remove = button.remove === 'remove' ? 1 : 0;
                answer[key].proof = answer[key].remove ? 0 : answer[key].proof;
                answer[key].break = answer[key].remove ? 0 : answer[key].break;
              }
            });
            this.form.metadata.next = this.nextQuestion === 'next' ? 1 : 0;
            this.form.metadata.randomize = this.randomizeButtons === 'randomize' ? 1 : 0;
            this.form.metadata.answer = answer;
          }

          this.$store.dispatch(`prestart_checklist/${action}`, this.form).then((data) => {
            if (isObject(data)) {
              this.$swal('Success', 'Your question has been saved', 'info').then(() => {
                this.$router.push('/admin/prestart_checklist');
              });
            } else {
              this.$swal('Failed', 'Your question failed to save', 'error');
            }
          });
        }
      });
    },
    validate() {
      return this.$validator.validateAll();
    },
    onCancel() {
      this.$router.push('/admin/prestart_checklist');
    },
    onChangeDefaultChoiceAnswer(val) {
      this.defaultChoiceAnswer = val;
    },
    onChangeDefaultMultiChoiceAnswer(val, textValue) {
      if (isBoolean(val)) {
        const removeIdx = this.defaultMultiChoiceAnswer.findIndex(dt => dt === textValue);
        this.defaultMultiChoiceAnswer.splice(removeIdx, 1);
      } else {
        this.defaultMultiChoiceAnswer.push(textValue);
      }
    },
    addAnswerChoice(typeAnswer) {
      let len = 1;
      if (typeAnswer === 'CHOICE') {
        len = this.optionsChoiceAnswer.length;
        this.optionsChoiceAnswer.push({ text: `Option ${len + 1}` });
      } else if (typeAnswer === 'MULTI_CHOICE') {
        len = this.optionsMultiChoiceAnswer.length;
        this.optionsMultiChoiceAnswer.push({ text: `Option ${len + 1}` });
      }
    },
    removeAnswerChoice(typeAnswer, index) {
      if (typeAnswer === 'CHOICE') {
        if (this.optionsChoiceAnswer.length > 1) {
          this.optionsChoiceAnswer.splice(index, 1);
        }
      } else if (typeAnswer === 'MULTI_CHOICE') {
        if (this.optionsMultiChoiceAnswer.length > 1) {
          this.optionsMultiChoiceAnswer.splice(index, 1);
        }
      }
    },
  },
};
</script>
<style lang="sass">
@import "~vue-multiselect/dist/vue-multiselect.min"
.multiselect-danger .multiselect__tags
  border: 1px solid #f86c6b
</style>



// WEBPACK FOOTER //
// src/views/pages/admin/prestart_checklist/CreateEdit.vue